/** @format */

import useAxios from '@/hooks/useAxios.hook';
import { RootState } from '@/redux/store';
import { endPoint, itemsOf } from '@/utils';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const useCategoryList = (
  categoryCode: string,
  initialData?: any[],
  initialEventCategoryName?: string,
) => {
  // Server-seeded first batch (so crawlers/AI see events in the HTML). The
  // client still refetches with the user's location on mount / location change.
  const hasInitialData = (initialData?.length ?? 0) > 0;
  const DEFAULT_LIMIT = 10;
  const [initialState, setInitialState] = useState({
    limit: DEFAULT_LIMIT,
    // If the server already rendered page 1, the next fetch must start
    // at page 2 — otherwise the first loadMore() re-requests skip 0 and
    // duplicates the events already on screen.
    skip: hasInitialData ? DEFAULT_LIMIT : 0,
    refreshing: false,
    data: hasInitialData ? (initialData as any[]) : [],
    hasMore: true,
    eventCategoryName: initialEventCategoryName || 'All Events',
  });
  const { skip, refreshing, data, hasMore, eventCategoryName } = initialState;
  const { fetchData, loading } = useAxios(!hasInitialData);
  // Scroll/resize can fire loadMore several times before the previous
  // request's state update commits; this ref blocks re-entrant calls
  // synchronously, which `refreshing` state (async) can't do.
  const isFetchingMoreRef = useRef(false);
  // InfiniteScroll can still invoke a stale loadMore closure (from before
  // its listener was swapped in) after a real scroll event. Reading skip
  // via this ref instead of the closed-over `skip` above ensures even a
  // stale call fetches the current next page rather than repeating one.
  const stateRef = useRef(initialState);
  stateRef.current = initialState;
  const updateState = (newState: any) => {
    setInitialState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  };
  const location = useSelector((state: RootState) => state.location);
  const coordinates = [location?.lng, location?.lat];

  const fetchSearchData = async ({
    limit = 10,
    skip,
  }: {
    limit?: number;
    skip: number;
  }) => {
    isFetchingMoreRef.current = true;
    try {
      const res = await fetchData({
        url: endPoint.FETCH_EVENTS_BY_CATEGORY_BY_PAGE_GUEST,
        params: {
          limit,
          skip,
        },
        method: 'POST',
        data: { categoryCode, coordinates },
        loading,
      });
      updateState({
        data: itemsOf(res?.data),
        skip: limit,
        hasMore: itemsOf(res?.data).length >= limit,
        eventCategoryName: res?.data?.eventCategoryName || eventCategoryName,
      });
    } finally {
      isFetchingMoreRef.current = false;
    }
  };
  const loadMore = async () => {
    const current = stateRef.current;
    if (current.hasMore && current.data?.length && !isFetchingMoreRef.current) {
      isFetchingMoreRef.current = true;
      updateState({
        refreshing: true,
      });
      try {
        const res = await fetchData({
          url: endPoint.FETCH_EVENTS_BY_CATEGORY_BY_PAGE_GUEST,
          params: {
            limit: current.limit,
            skip: current.skip,
          },
          method: 'POST',
          data: { categoryCode },
          loading: false,
        });
        const newEvents = itemsOf(res?.data);
        setInitialState((prev) => ({
          ...prev,
          refreshing: false,
          skip: prev.skip + prev.limit,
          hasMore: newEvents.length >= prev.limit,
          data: [...prev.data, ...newEvents],
        }));
      } finally {
        isFetchingMoreRef.current = false;
      }
    }
  };
  useEffect(() => {    
    fetchSearchData({ limit: 10, skip: 0 });
  }, [categoryCode, location]);

  return {
    loading,
    data,
    refreshing,
    hasMore,
    loadMore,
    skip,
    eventCategoryName,
  };
};

export default useCategoryList;
